import { Card } from "@/components/ui/Card";
import { EstimateCard } from "@/components/EstimateCard";
import { ApprovalCard } from "@/components/ApprovalCard";
import { ChatCardRouter } from "@/components/cards/ChatCardRouter";
import type { ChatCardKind } from "@/components/cards/card-data";
import type { LoggingFlowReviewItem } from "@/lib/logging-flow";
import { cn } from "@/lib/cn";
import { Search, Brain, Pencil } from "lucide-react";
import type { ApprovalContinuationStatus } from "@/screens/approval-recovery";
import { APP_LAYOUT } from "@/lib/app-layout";
import { renderMarkdown } from "@/lib/markdown";

export type ChatItem =
  | {
      kind: "text";
      role: "user" | "assistant";
      text: string;
      imageUrl?: string;
      messageId: string;
      messageIndex: number;
    }
  | {
      kind: "tool-call";
      toolName: string;
      args: unknown;
      messageId: string;
      messageIndex: number;
    }
  | {
      kind: "card";
      cardKind: ChatCardKind;
      toolName: string;
      args: unknown;
      result: unknown;
      messageId: string;
      messageIndex: number;
    }
  | {
      kind: "approval-request";
      approvalId: string;
      approvalGroupId: string;
      toolName: string;
      args: unknown;
      messageId: string;
      messageIndex: number;
      approvalStatus?: ApprovalContinuationStatus;
      retryLabel?: string;
      retryApproved?: boolean;
      retryEdit?: boolean;
      responseMessageId?: string;
      continuationMessageStatus?: "pending" | "failed";
    };

export interface MessageBubbleProps {
  item: ChatItem;
  index: number;
  onApprove?: (
    approvalId: string,
    approved: boolean,
    messageId: string,
    reason?: string,
  ) => Promise<boolean>;
  onEdit?: (
    approvalId: string,
    messageId: string,
    template: string,
  ) => Promise<boolean>;
  onSwitchScreen?: (screen: string) => void;
  onSendMessage?: (text: string) => void;
  onStartReview?: (items: readonly LoggingFlowReviewItem[]) => void;
  onRespondCheckin?: (checkinId: string, accept: boolean) => Promise<unknown>;
}

function getStatusChip(toolName: string) {
  if (toolName.includes("search")) {
    return { icon: Search, label: "searching" };
  }
  if (toolName.includes("estimate")) {
    return { icon: Brain, label: "estimating" };
  }
  return { icon: Pencil, label: "logging" };
}

export function MessageBubble({
  item,
  index,
  onApprove,
  onEdit,
  onSwitchScreen,
  onSendMessage,
  onStartReview,
  onRespondCheckin,
}: MessageBubbleProps) {
  if (item.kind === "approval-request") {
    return (
      <div
        className="flex w-full justify-start"
        data-testid={`message-bubble-${index}`}
      >
        <ApprovalCard
          approvalId={item.approvalId}
          toolName={item.toolName}
          args={item.args}
          continuationStatus={item.approvalStatus}
          retryLabel={item.retryLabel}
          retryEdit={item.retryEdit}
          onApprove={() => onApprove?.(item.approvalId, item.retryApproved ?? true, item.messageId) ?? Promise.resolve(false)}
          onReject={() => onApprove?.(item.approvalId, false, item.messageId, "User rejected the proposed action") ?? Promise.resolve(false)}
          onEdit={(template) => onEdit?.(item.approvalId, item.messageId, template) ?? Promise.resolve(false)}
        />
      </div>
    );
  }

  if (item.kind === "card") {
    return (
      <div
        className="flex w-full justify-start"
        data-testid={`message-bubble-${index}`}
      >
        <ChatCardRouter
          cardKind={item.cardKind}
          toolName={item.toolName}
          args={item.args}
          result={item.result}
          {...(onSwitchScreen !== undefined ? { onSwitchScreen } : {})}
          {...(onSendMessage !== undefined ? { onSendMessage } : {})}
          {...(onStartReview !== undefined ? { onStartReview } : {})}
          {...(onRespondCheckin !== undefined ? { onRespondCheckin } : {})}
        />
      </div>
    );
  }

  if (item.kind === "tool-call") {
    const { icon: Icon, label } = getStatusChip(item.toolName);
    return (
      <div
        className="flex w-full flex-col items-start gap-1"
        data-testid={`message-bubble-${index}`}
      >
        <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-text-muted">
          <Icon size={14} strokeWidth={2} />
          <span>{label}</span>
        </div>
        <EstimateCard toolName={item.toolName} args={item.args} />
      </div>
    );
  }

  const isUser = item.role === "user";
  return (
    <div
      className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}
      data-testid={`message-bubble-${index}`}
    >
      <Card
        className={cn(
          "max-w-[85%] px-4 py-2.5 shadow-none",
          APP_LAYOUT.messageTextClass,
          isUser
            ? "rounded-2xl rounded-br-md border border-accent-light bg-accent-light text-text"
            : "rounded-2xl rounded-bl-md border border-border bg-surface text-text",
        )}
      >
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt="Attached meal"
            className="mb-2 max-h-56 w-full rounded-xl object-cover"
            data-testid="message-image"
          />
        )}
        <div className="min-w-0">{renderMarkdown(item.text)}</div>
      </Card>
    </div>
  );
}
